import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSun, FiMoon, FiHexagon } from 'react-icons/fi';
import { useEditorStore } from '../../stores/editorStore';
import { useProjectStore } from '../../stores/projectStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import styles from './TopBar.module.css';

export function TopBar() {
  const { t } = useTranslation();
  const theme = useEditorStore((s) => s.theme);
  const toggleTheme = useEditorStore((s) => s.toggleTheme);
  const projectName = useProjectStore((s) => s.project.name);
  const setProjectName = useProjectStore((s) => s.setProjectName);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(projectName);

  const handleStartEdit = useCallback(() => {
    setNameValue(projectName);
    setEditingName(true);
  }, [projectName]);

  const handleCommitName = useCallback(() => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== projectName) {
      setProjectName(trimmed);
    } else {
      setNameValue(projectName);
    }
    setEditingName(false);
  }, [nameValue, projectName, setProjectName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleCommitName();
      if (e.key === 'Escape') {
        setNameValue(projectName);
        setEditingName(false);
      }
    },
    [handleCommitName, projectName]
  );

  return (
    <header className={styles.topBar}>
      <div className={styles.logo}>
        <FiHexagon className={styles.logoIcon} />
        <span>{t('app.title')}</span>
      </div>

      <div className={styles.divider} />

      {editingName ? (
        <input
          className={styles.projectName}
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onBlur={handleCommitName}
          onKeyDown={handleKeyDown}
          autoFocus
          spellCheck={false}
        />
      ) : (
        <button
          className={styles.projectName}
          onClick={handleStartEdit}
          title={t('editor.rename')}
        >
          {projectName}
        </button>
      )}

      <div className={styles.spacer} />

      <div className={styles.actions}>
        <button
          className={styles.iconBtn}
          onClick={toggleTheme}
          title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
        >
          {theme === 'dark' ? <FiSun /> : <FiMoon />}
        </button>

        <LanguageSwitcher />
      </div>
    </header>
  );
}
